/**
 * BillingSettings - Shows current plan, usage, and upgrade options
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  ArrowUpCircleIcon,
  SparklesIcon,
  ServerIcon,
  TableCellsIcon,
  UsersIcon,
  DocumentCheckIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import { useBilling } from '../../contexts/BillingContext';
import { LimitWarning } from '../../components/billing';

// Plan feature descriptions
const PLAN_FEATURES = {
  free: [
    '1 Connection',
    '1 Schema',
    '15 Profiled Tables',
    '10 Manual Rules',
    'Email Alerts Only',
    '7 Day History',
  ],
  starter: [
    '2 Connections',
    '5 Schemas',
    '50 Profiled Tables',
    '50 Manual Rules',
    'Anomaly Detection',
    'Basic Lineage',
    '30 Day History',
  ],
  growth: [
    '5 Connections',
    '20 Schemas',
    '200 Profiled Tables',
    'Unlimited Rules',
    '25 Custom SQL Rules',
    'dbt Integration',
    'Full Lineage',
    'All Alert Channels',
    '90 Day History',
  ],
  scale: [
    'Unlimited Connections',
    'Unlimited Schemas',
    'Unlimited Tables',
    'Unlimited Rules',
    'SSO/SAML',
    'Audit Logs',
    '1 Year History',
    'Priority Support',
  ],
};

const PLAN_PRICES = {
  free: { monthly: 0, annual: 0 },
  starter: { monthly: 299, annual: 2990 },
  growth: { monthly: 999, annual: 9990 },
  scale: { monthly: 1999, annual: 19990 },
};

const BillingSettings = () => {
  const {
    loading,
    error,
    planTier,
    planDisplayName,
    isFoundingMember,
    getLimitStatus,
    getUpgradeUrl,
    canUpgrade,
    refresh,
  } = useBilling();

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-secondary-600">Loading billing information...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-700">{error}</p>
          <button
            onClick={refresh}
            className="mt-2 text-sm text-danger-600 hover:text-danger-800 font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const connectionStatus = getLimitStatus('connections');
  const schemaStatus = getLimitStatus('schemas');
  const tableStatus = getLimitStatus('profiled_tables');
  const userStatus = getLimitStatus('users');
  const ruleStatus = getLimitStatus('manual_rules');
  const sqlRuleStatus = getLimitStatus('custom_sql_rules');

  const formatLimit = (status) => {
    if (status.isUnlimited) return 'Unlimited';
    return `${status.current} / ${status.max}`;
  };

  const getProgressColor = (status) => {
    if (status.isUnlimited) return 'bg-accent-500';
    if (status.percentage >= 100) return 'bg-danger-500';
    if (status.percentage >= 80) return 'bg-warning-500';
    return 'bg-primary-500';
  };

  const getProgressWidth = (status) => {
    if (status.isUnlimited) return 100;
    return Math.min(status.percentage || 0, 100);
  };

  return (
    <div className="p-6 space-y-8">
      {/* Current Plan Section */}
      <div>
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Current Plan</h2>

        <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-lg border border-primary-200 p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-2xl font-bold text-secondary-900">
                  {planDisplayName}
                </h3>
                {isFoundingMember && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                    <SparklesIcon className="h-4 w-4 mr-1" />
                    Founding Member
                  </span>
                )}
              </div>

              <p className="mt-1 text-secondary-600">
                {planTier === 'free' && 'Get started with data quality monitoring'}
                {planTier === 'starter' && 'For teams getting started with data observability'}
                {planTier === 'growth' && 'For teams scaling their data infrastructure'}
                {planTier === 'scale' && 'Enterprise-grade data quality platform'}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {PLAN_FEATURES[planTier]?.slice(0, 4).map((feature, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white text-secondary-700"
                  >
                    <CheckCircleIcon className="h-3.5 w-3.5 mr-1 text-accent-500" />
                    {feature}
                  </span>
                ))}
              </div>
            </div>

            <div className="text-right">
              <p className="text-3xl font-bold text-secondary-900">
                ${PLAN_PRICES[planTier]?.monthly || 0}
                <span className="text-base font-normal text-secondary-500">/mo</span>
              </p>
              {PLAN_PRICES[planTier]?.monthly > 0 && (
                <p className="text-sm text-secondary-500">
                  or ${PLAN_PRICES[planTier]?.annual}/year (save 17%)
                </p>
              )}
            </div>
          </div>

          {canUpgrade() && (
            <div className="mt-6 pt-6 border-t border-primary-200">
              <Link
                to={getUpgradeUrl()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
              >
                <ArrowUpCircleIcon className="h-5 w-5 mr-2" />
                Upgrade Plan
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Usage Overview Section */}
      <div>
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Usage Overview</h2>

        {/* Limit warnings */}
        <div className="space-y-3 mb-6">
          <LimitWarning limit="connections" />
          <LimitWarning limit="schemas" />
          <LimitWarning limit="profiled_tables" />
          <LimitWarning limit="manual_rules" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Connections */}
          <div className="bg-white border border-secondary-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ServerIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-900">Connections</p>
                <p className="text-xs text-secondary-500">{formatLimit(connectionStatus)}</p>
              </div>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor(connectionStatus)}`}
                style={{ width: `${getProgressWidth(connectionStatus)}%` }}
              />
            </div>
          </div>

          {/* Schemas */}
          <div className="bg-white border border-secondary-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TableCellsIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-900">Schemas</p>
                <p className="text-xs text-secondary-500">{formatLimit(schemaStatus)}</p>
              </div>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor(schemaStatus)}`}
                style={{ width: `${getProgressWidth(schemaStatus)}%` }}
              />
            </div>
          </div>

          {/* Profiled Tables */}
          <div className="bg-white border border-secondary-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TableCellsIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-900">Profiled Tables</p>
                <p className="text-xs text-secondary-500">{formatLimit(tableStatus)}</p>
              </div>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor(tableStatus)}`}
                style={{ width: `${getProgressWidth(tableStatus)}%` }}
              />
            </div>
          </div>

          {/* Users */}
          <div className="bg-white border border-secondary-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <UsersIcon className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-900">Users</p>
                <p className="text-xs text-secondary-500">{formatLimit(userStatus)}</p>
              </div>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor(userStatus)}`}
                style={{ width: `${getProgressWidth(userStatus)}%` }}
              />
            </div>
          </div>

          {/* Manual Rules */}
          <div className="bg-white border border-secondary-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <DocumentCheckIcon className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-900">Manual Rules</p>
                <p className="text-xs text-secondary-500">{formatLimit(ruleStatus)}</p>
              </div>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor(ruleStatus)}`}
                style={{ width: `${getProgressWidth(ruleStatus)}%` }}
              />
            </div>
          </div>

          {/* Custom SQL Rules */}
          <div className="bg-white border border-secondary-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-rose-100 rounded-lg">
                <CodeBracketIcon className="h-5 w-5 text-rose-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-900">Custom SQL Rules</p>
                <p className="text-xs text-secondary-500">{formatLimit(sqlRuleStatus)}</p>
              </div>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor(sqlRuleStatus)}`}
                style={{ width: `${getProgressWidth(sqlRuleStatus)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Plan Comparison Section */}
      {canUpgrade() && (
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Compare Plans</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {['free', 'starter', 'growth', 'scale'].map((tier) => {
              const isCurrentPlan = tier === planTier;
              const tierNames = {
                free: 'Free',
                starter: 'Starter',
                growth: 'Growth',
                scale: 'Scale',
              };

              return (
                <div
                  key={tier}
                  className={`relative bg-white rounded-lg border-2 p-6 ${
                    isCurrentPlan
                      ? 'border-primary-500 ring-2 ring-primary-200'
                      : 'border-secondary-200'
                  }`}
                >
                  {isCurrentPlan && (
                    <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
                      Current Plan
                    </span>
                  )}

                  <h3 className="text-lg font-semibold text-secondary-900">
                    {tierNames[tier]}
                  </h3>
                  <p className="mt-2 text-2xl font-bold text-secondary-900">
                    ${PLAN_PRICES[tier]?.monthly || 0}
                    <span className="text-sm font-normal text-secondary-500">/mo</span>
                  </p>

                  <ul className="mt-4 space-y-2">
                    {PLAN_FEATURES[tier]?.slice(0, 5).map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-secondary-600">
                        <CheckCircleIcon className="h-4 w-4 text-accent-500 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {!isCurrentPlan && tier !== 'free' && (
                    <Link
                      to={getUpgradeUrl(tier)}
                      className="mt-6 block w-full text-center px-4 py-2 border border-primary-600 text-sm font-medium rounded-md text-primary-600 hover:bg-primary-50"
                    >
                      {['free', 'starter'].includes(planTier) && tier === 'starter'
                        ? 'Upgrade'
                        : 'Contact Sales'}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-secondary-50 rounded-lg p-6">
        <h3 className="text-sm font-medium text-secondary-900">Need help with billing?</h3>
        <p className="mt-1 text-sm text-secondary-600">
          Contact our support team at{' '}
          <a href="mailto:support@sparvi.io" className="text-primary-600 hover:text-primary-700">
            support@sparvi.io
          </a>{' '}
          or check out our{' '}
          <a href="/documentation" className="text-primary-600 hover:text-primary-700">
            documentation
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default BillingSettings;
